/**
 * LAYOUT-1 — realistic office floor plan. Pure TS data + the stable
 * hash-slot allocator for station entities. No PlayCanvas dependency.
 *
 * Coordinate system: +X east, +Z south, origin at room centre, 1u = 1m.
 * Expanded HQ shell is 48 × 30 m (walls at x = ±24, z = ±15). The Core and
 * atrium keep their authored coordinates; the added perimeter is real program
 * and circulation space rather than a scale-up of avatars or furniture.
 */

// ── Room shell ────────────────────────────────────────────────────────────

export const ROOM_HALF_X = 24
export const ROOM_HALF_Z = 15
/**
 * Finished ceiling height. Real commercial offices are 2.7-3.0 m; at 4 m the
 * 2.4 m entry door leaves a 1.6 m header, which the eye reads as "too big to
 * be an office". Every other vertical dimension below is a real-world scale
 * cue and must NOT be scaled with it.
 */
export const WALL_HEIGHT = 3
export const WALL_THICKNESS = 0.3          // exterior walls
export const INNER_WALL_THICKNESS = 0.1    // meeting/server room partition walls
export const ENTRY_DOOR_HALF_WIDTH = 1.2   // double door, 2.4 m, centred on south wall

/** Inner faces of the exterior walls (usable floor area). */
export const INNER_HALF_X = ROOM_HALF_X - WALL_THICKNESS / 2
export const INNER_HALF_Z = ROOM_HALF_Z - WALL_THICKNESS / 2

// ── Desk clusters ─────────────────────────────────────────────────────────
// 5 clusters × 4 desks. Each cluster is a 2×2 arrangement: two back-to-back
// desk pairs facing each other across an east-west spine. Footprint 4 × 3.6 m.

export const CLUSTER_CENTERS: ReadonlyArray<readonly [number, number]> = [
  [-10, -6], [-3, -6], [4, -6], [-10, 2], [-3, 2],
]

export const DESKS_PER_CLUSTER = 4
export const GRID_SLOTS = CLUSTER_CENTERS.length * DESKS_PER_CLUSTER // 20

export const DESK_PAIR_DX = 1.0  // half the spacing between the two desk columns
export const DESK_PAIR_DZ = 0.9  // half the spacing between the two desk rows

/** Station-unit AABB half extents (desk + tucked chair + walk-up space). */
export const STATION_HALF_W = 1.0
export const STATION_HALF_D = 0.9

/** Cluster AABB half extents: 2 stations wide, 2 stations deep. */
export const CLUSTER_HALF_X = DESK_PAIR_DX + STATION_HALF_W // 2.0 → 4.0 m
export const CLUSTER_HALF_Z = DESK_PAIR_DZ + STATION_HALF_D // 1.8 → 3.6 m

/**
 * Per-slot offsets inside a cluster. rotY 0 faces north (-Z); rotY 180 faces
 * south (+Z) — the two rows face each other across the spine. Desks share
 * exact cluster alignment: NO jitter is ever applied to stations.
 */
export const DESK_OFFSETS: ReadonlyArray<{ dx: number; dz: number; rotY: number }> = [
  { dx: -DESK_PAIR_DX, dz: -DESK_PAIR_DZ, rotY: 180 },
  { dx:  DESK_PAIR_DX, dz: -DESK_PAIR_DZ, rotY: 180 },
  { dx: -DESK_PAIR_DX, dz:  DESK_PAIR_DZ, rotY: 0 },
  { dx:  DESK_PAIR_DX, dz:  DESK_PAIR_DZ, rotY: 0 },
]

export function slotToPosition(slot: number): [number, number, number] {
  const cluster = Math.floor(slot / DESKS_PER_CLUSTER) % CLUSTER_CENTERS.length
  const off = DESK_OFFSETS[((slot % DESKS_PER_CLUSTER) + DESKS_PER_CLUSTER) % DESKS_PER_CLUSTER]
  const [cx, cz] = CLUSTER_CENTERS[cluster]
  return [cx + off.dx, 0, cz + off.dz]
}

/** World Y rotation (degrees) for a slot's station. Deterministic, no jitter. */
export function slotRotationY(slot: number): number {
  return DESK_OFFSETS[((slot % DESKS_PER_CLUSTER) + DESKS_PER_CLUSTER) % DESKS_PER_CLUSTER].rotY
}

/** Axis-aligned XZ bounding box of a cluster. */
export function clusterAABB(index: number): { minX: number; maxX: number; minZ: number; maxZ: number } {
  const [cx, cz] = CLUSTER_CENTERS[index]
  return {
    minX: cx - CLUSTER_HALF_X, maxX: cx + CLUSTER_HALF_X,
    minZ: cz - CLUSTER_HALF_Z, maxZ: cz + CLUSTER_HALF_Z,
  }
}

// ── Circulation ───────────────────────────────────────────────────────────

export const MIN_WALKWAY = 2.4
/** Main east-west aisle: 3 m wide, centred on z = -2. */
export const MAIN_AISLE = { z: -2, halfWidth: 1.5 }

// ── Zones ─────────────────────────────────────────────────────────────────

export interface ZoneRect { minX: number; maxX: number; minZ: number; maxZ: number }

/** Glass-walled meeting room, NE. Door gap in the south glass wall. */
export const MEETING_ROOM: ZoneRect = { minX: 14, maxX: 22, minZ: -14, maxZ: -8 }
export const MEETING_DOOR = { minX: 17.4, maxX: 18.6 } // gap in wall at z = MEETING_ROOM.maxZ

/** Kitchen / break area, SE. Open plan (no partition walls). */
export const KITCHEN: ZoneRect = { minX: 15, maxX: 23, minZ: 7, maxZ: 14 }
export const KITCHEN_CENTER: readonly [number, number] = [19, 10.5]

/** Enclosed server room, NW. Interior glass wall on its east side. */
export const SERVER_ROOM: ZoneRect = { minX: -23, maxX: -18, minZ: -14, maxZ: -10 }
export const SERVER_DOOR = { minX: -21.1, maxX: -19.9 } // gap in wall at z = SERVER_ROOM.maxZ

// Server-room system anchors (obelisk + robot attendant + local light).
export const SERVER_OBELISK_POS: readonly [number, number] = [-22.3, -10.7]
export const SERVER_ROBOT_POS: readonly [number, number] = [-20.5, -12.4]
export const SERVER_ROOM_LIGHT_POS: readonly [number, number, number] = [-20.5, 3.2, -12]

// ── Walls (render + collider source of truth) ─────────────────────────────

export type WallMaterial = 'cream' | 'glass'

export interface WallSegment {
  id: string
  minX: number; maxX: number
  minZ: number; maxZ: number
  minY: number; maxY: number
  material: WallMaterial
}

function wallX(id: string, x: number, minZ: number, maxZ: number, t: number, material: WallMaterial): WallSegment {
  return { id, minX: x - t / 2, maxX: x + t / 2, minZ, maxZ, minY: 0, maxY: WALL_HEIGHT, material }
}

function wallZ(id: string, z: number, minX: number, maxX: number, t: number, material: WallMaterial): WallSegment {
  return { id, minX, maxX, minZ: z - t / 2, maxZ: z + t / 2, minY: 0, maxY: WALL_HEIGHT, material }
}

/** Clear height of the south entry opening — a lintel closes the wall above. */
export const ENTRY_DOOR_HEIGHT = 2.4

/**
 * Every wall segment in the floor plan. Door gaps are expressed as missing
 * segments: the south entry (x ∈ ±ENTRY_DOOR_HALF_WIDTH), the meeting-room
 * door (MEETING_DOOR) and the server-room door (SERVER_DOOR).
 */
export function computeWallSegments(): WallSegment[] {
  const T = WALL_THICKNESS
  const IT = INNER_WALL_THICKNESS
  const M = MEETING_ROOM
  const S = SERVER_ROOM
  return [
    // Exterior shell — entry double-door gap centred on the south wall.
    wallZ('wall-north', -ROOM_HALF_Z, -ROOM_HALF_X, ROOM_HALF_X, T, 'cream'),
    wallZ('wall-south-west', ROOM_HALF_Z, -ROOM_HALF_X, -ENTRY_DOOR_HALF_WIDTH, T, 'cream'),
    wallZ('wall-south-east', ROOM_HALF_Z, ENTRY_DOOR_HALF_WIDTH, ROOM_HALF_X, T, 'cream'),
    // Lintel over the entry — closes the wall above door height so the wall
    // clock has something to hang on. Stays clear of the walkable band.
    { ...wallZ('entry-lintel', ROOM_HALF_Z, -ENTRY_DOOR_HALF_WIDTH, ENTRY_DOOR_HALF_WIDTH, T, 'cream'), minY: ENTRY_DOOR_HEIGHT },
    wallX('wall-west', -ROOM_HALF_X, -ROOM_HALF_Z, ROOM_HALF_Z, T, 'cream'),
    // East frontage is the bay view: a full floor-to-ceiling glass curtain wall
    // (frosted spandrel + clear vision glass) looking out over the skybox.
    wallX('wall-east', ROOM_HALF_X, -ROOM_HALF_Z, ROOM_HALF_Z, T, 'glass'),

    // Meeting room — glass on all four sides, door gap in the south wall.
    wallX('meeting-west', M.minX, M.minZ, M.maxZ, IT, 'glass'),
    wallX('meeting-east', M.maxX, M.minZ, M.maxZ, IT, 'glass'),
    wallZ('meeting-north', M.minZ, M.minX, M.maxX, IT, 'glass'),
    wallZ('meeting-south-west', M.maxZ, M.minX, MEETING_DOOR.minX, IT, 'glass'),
    wallZ('meeting-south-east', M.maxZ, MEETING_DOOR.maxX, M.maxX, IT, 'glass'),

    // Server room — solid partitions, glass interior wall on the east side,
    // door gap in the south wall.
    wallX('server-west', S.minX, S.minZ, S.maxZ, IT, 'cream'),
    wallZ('server-north', S.minZ, S.minX, S.maxX, IT, 'cream'),
    wallX('server-east', S.maxX, S.minZ, S.maxZ, IT, 'glass'),
    wallZ('server-south-west', S.maxZ, S.minX, SERVER_DOOR.minX, IT, 'cream'),
    wallZ('server-south-east', S.maxZ, SERVER_DOOR.maxX, S.maxX, IT, 'cream'),
  ]
}

// ── East-wall windows ─────────────────────────────────────────────────────
// Human-scale window rhythm on the expanded east wall (x = +24).

// Real sill/head: 0.90 sill, 1.80 tall -> head at 2.70, which sets the daylit
// depth (1.5-2.5x head height = 4-6.75 m in from the glass).
export const WINDOW_SILL = 0.9
export const WINDOW_HEIGHT = 1.8
// 4 m panels on a 4 m pitch formed ONE unbroken 24 m light band with no
// mullions — the clearest "this is a game box" tell on the east wall.
export const WINDOW_WIDTH = 1.8
/** Gap between window panels — the mullion the old plan had none of. */
export const WINDOW_MULLION = 0.15
export const WINDOW_X = ROOM_HALF_X - WALL_THICKNESS / 2 - 0.05 // just inside the east wall
export const WINDOW_PANEL_Z: readonly number[] = [
  -13.65, -11.7, -9.75, -7.8, -5.85, -3.9, -1.95, 0,
  1.95, 3.9, 5.85, 7.8, 9.75, 11.7, 13.65,
]

// ── Structural columns ────────────────────────────────────────────────────
// A 48x30 m floorplate in a real building has columns on a 6-9 m grid. Their
// absence was the clearest "this is a game box" tell. They double as free
// sightline breaks and human-scale references.

/** Square column side (m). */
export const COLUMN_SIZE = 0.45

/** Column centres [x, z], tuned to land in aisles rather than in desks. */
export const COLUMN_POSITIONS: ReadonlyArray<readonly [number, number]> = [
  [-14.4, -9.2], [-14.4, 0.4], [-14.4, 9.2],
  [-6.6, 9.2],
  // The former (0.6, 6.4) member stood 3.1 m in front of the HQ spawn and
  // filled the normal-eye-height arrival view. The upper grid already omits
  // this atrium/entry axis; L0 follows the same clear focal corridor.
  [0.6, -9.2],
  [7.4, -9.2], [7.4, 0.4],
  [15.2, -9.2], [15.2, 0.4],
]

// ── Pendant lights ────────────────────────────────────────────────────────
// One pendant fixture over every desk cluster plus the kitchen.

export const PENDANT_Y = 2.55
export const PENDANT_POSITIONS: ReadonlyArray<readonly [number, number]> = [
  ...CLUSTER_CENTERS,
  KITCHEN_CENTER,
]

// ── Ceiling fixtures (LAYOUT-2) ───────────────────────────────────────────
// One visible ceiling fixture + local light per zone: every desk cluster,
// the kitchen, the meeting room and the server room.

export const MEETING_CENTER: readonly [number, number] = [18, -11]
export const SERVER_CENTER: readonly [number, number] = [
  SERVER_ROOM_LIGHT_POS[0], SERVER_ROOM_LIGHT_POS[2],
]

/** Y for the prop_lightCeiling GLB (flush with the ceiling). */
export const FIXTURE_MOUNT_Y = WALL_HEIGHT - 0.02
/** Y for the emissive diffuser panel (guaranteed-visible fallback). */
export const FIXTURE_GLOW_Y = WALL_HEIGHT - 0.09

export interface CeilingFixture { id: string; x: number; z: number }

export const CEILING_FIXTURES: ReadonlyArray<CeilingFixture> = [
  ...CLUSTER_CENTERS.map(([x, z], i) => ({ id: `cluster-${i}`, x, z })),
  { id: 'kitchen', x: KITCHEN_CENTER[0], z: KITCHEN_CENTER[1] },
  { id: 'meeting', x: MEETING_CENTER[0], z: MEETING_CENTER[1] },
  { id: 'server', x: SERVER_CENTER[0], z: SERVER_CENTER[1] },
]

/** Meeting-room fixture light is on only when more than this many runs are active. */
export const MEETING_LIGHT_MIN_ACTIVE = 5

export function meetingLightOn(activeRunCount: number): boolean {
  return activeRunCount > MEETING_LIGHT_MIN_ACTIVE
}

// ── Skirting (LAYOUT-2) ───────────────────────────────────────────────────
// Charcoal strip along the base of every solid wall — covers the floor/wall
// seam. Derived from the cream wall segments (glass walls meet the floor
// with a frosted band already; the entry lintel floats above the door).

export const SKIRTING_HEIGHT = 0.12
export const SKIRTING_OVERHANG = 0.03

export interface SkirtingSegment {
  id: string
  minX: number; maxX: number
  minZ: number; maxZ: number
  height: number
}

export function computeSkirtingSegments(): SkirtingSegment[] {
  const O = SKIRTING_OVERHANG
  return computeWallSegments()
    .filter((s) => s.material === 'cream' && s.minY === 0)
    .map((s) => ({
      id: `skirt-${s.id}`,
      minX: s.minX - O, maxX: s.maxX + O,
      minZ: s.minZ - O, maxZ: s.maxZ + O,
      height: SKIRTING_HEIGHT,
    }))
}

// ── Texture tiling (LAYOUT-2) ─────────────────────────────────────────────
// Real-world repeat sizes in metres — consumed by scene.ts to derive UV
// tiling from surface dimensions.

export const FLOOR_PLANK_REPEAT_M = 2.0   // wood plank repeat
export const CARPET_REPEAT_M = 1.5        // meeting-room carpet
export const CEILING_TILE_REPEAT_M = 0.6  // suspended-ceiling grid

// ── Furniture placements ──────────────────────────────────────────────────

export type Mount = 'floor' | 'desk' | 'wall' | 'stack'

export interface PropPlacement {
  id: string
  /** GLB filename from the asset manifest, or null for procedural geometry. */
  file: string | null
  x: number; y: number; z: number
  rotY: number
  /** Post-rotation axis-aligned XZ footprint half extents. */
  halfW: number; halfD: number
  /** Furniture group — items in the same group may interpenetrate (tucked). */
  group: string
  mount: Mount
  /** Uniform model scale (default 1). Kenney furniture is authored small. */
  scale?: number
  /** Décor items get deterministic seeded jitter (±5 cm / ±10°) at spawn. */
  decor?: boolean
  /** Walkable flat items (rugs) — exempt from overlap constraints. */
  walkable?: boolean
}

export const DESK_SURFACE_Y = 0.75

/** Meeting room: simple table + 6 chairs. */
export const MEETING_FURNITURE: PropPlacement[] = [
  { id: 'meeting-table', file: null, x: 18, y: 0, z: -11, rotY: 0, halfW: 1.5, halfD: 0.7, group: 'meeting-table', mount: 'floor' },
  { id: 'meeting-chair-n1', file: 'prop_chairCushion.glb', x: 16.5, y: 0, z: -12.2, rotY: 180, halfW: 0.3, halfD: 0.3, group: 'meeting-chair-n1', mount: 'floor', scale: 1.35 },
  { id: 'meeting-chair-n2', file: 'prop_chairCushion.glb', x: 18, y: 0, z: -12.2, rotY: 180, halfW: 0.3, halfD: 0.3, group: 'meeting-chair-n2', mount: 'floor', scale: 1.35 },
  { id: 'meeting-chair-n3', file: 'prop_chairCushion.glb', x: 19.5, y: 0, z: -12.2, rotY: 180, halfW: 0.3, halfD: 0.3, group: 'meeting-chair-n3', mount: 'floor', scale: 1.35 },
  { id: 'meeting-chair-s1', file: 'prop_chairCushion.glb', x: 16.5, y: 0, z: -9.8, rotY: 0, halfW: 0.3, halfD: 0.3, group: 'meeting-chair-s1', mount: 'floor', scale: 1.35 },
  { id: 'meeting-chair-s2', file: 'prop_chairCushion.glb', x: 18, y: 0, z: -9.8, rotY: 0, halfW: 0.3, halfD: 0.3, group: 'meeting-chair-s2', mount: 'floor', scale: 1.35 },
  { id: 'meeting-chair-s3', file: 'prop_chairCushion.glb', x: 19.5, y: 0, z: -9.8, rotY: 0, halfW: 0.3, halfD: 0.3, group: 'meeting-chair-s3', mount: 'floor', scale: 1.35 },
]

/** Kitchen / break area: rug, sofas + low table, coffee corner, water cooler. */
export const KITCHEN_FURNITURE: PropPlacement[] = [
  { id: 'kitchen-rug', file: null, x: 19, y: 0, z: 10.5, rotY: 0, halfW: 2.5, halfD: 2.0, group: 'kitchen-rug', mount: 'floor', walkable: true },
  { id: 'kitchen-table', file: null, x: 19, y: 0, z: 10.5, rotY: 0, halfW: 0.6, halfD: 0.4, group: 'kitchen-table', mount: 'floor' },
  { id: 'kitchen-sofa-n', file: 'prop_loungeSofa.glb', x: 19, y: 0, z: 8.6, rotY: 180, halfW: 1.1, halfD: 0.5, group: 'kitchen-sofa-n', mount: 'floor', scale: 1.5 },
  { id: 'kitchen-sofa-s', file: 'prop_loungeDesignSofa.glb', x: 19, y: 0, z: 12.4, rotY: 0, halfW: 1.1, halfD: 0.5, group: 'kitchen-sofa-s', mount: 'floor', scale: 1.5 },
  { id: 'kitchen-coffee', file: 'prop_kitchenCoffeeMachine.glb', x: 16, y: 0, z: 13.4, rotY: 180, halfW: 0.3, halfD: 0.3, group: 'kitchen-coffee', mount: 'floor', scale: 1.3 },
  { id: 'kitchen-fridge', file: 'prop_kitchenFridgeSmall.glb', x: 17.2, y: 0, z: 13.4, rotY: 180, halfW: 0.4, halfD: 0.35, group: 'kitchen-fridge', mount: 'floor', scale: 1.3 },
  { id: 'kitchen-cooler', file: 'prop_waterCooler.glb', x: 22.5, y: 0, z: 13.4, rotY: 225, halfW: 0.3, halfD: 0.3, group: 'kitchen-cooler', mount: 'floor', decor: true },
]

/**
 * Standing spots for waiting queue figures in the break area: [x, y, z, rotY].
 * These used to be sofa seats at y=0.35 paired with a Y-squash on the model,
 * which left a straight-legged figure hovering above the cushion. The rig has
 * no seated clip, so the figures stand around the break area instead —
 * clustered near the coffee corner and cooler, clear of the sofa footprints
 * (sofas span z 6.6 / 9.4) so nothing interpenetrates the furniture.
 */
export const KITCHEN_SOFA_SEATS: ReadonlyArray<readonly [number, number, number, number]> = [
  [17.2, 0, 9.6, 120], [17.4, 0, 11.4, 75], [18.6, 0, 10.5, 200],
  [20.4, 0, 9.4, 250], [20.6, 0, 11.6, 290], [19.4, 0, 7.6, 160],
]

/** Server room: 2 server racks, cabinet desk, obelisk, small props. */
export const SERVER_FURNITURE: PropPlacement[] = [
  { id: 'server-rack-1', file: 'prop_serverComputerLarge.glb', x: -21.6, y: 0, z: -13.3, rotY: 0, halfW: 0.5, halfD: 0.4, group: 'server-rack-1', mount: 'floor' },
  { id: 'server-rack-2', file: 'prop_serverComputerLarge.glb', x: -20.2, y: 0, z: -13.3, rotY: 0, halfW: 0.5, halfD: 0.4, group: 'server-rack-2', mount: 'floor' },
  { id: 'server-cabinet', file: null, x: -20.5, y: 0, z: -11.6, rotY: 0, halfW: 1.0, halfD: 0.4, group: 'server-cabinet', mount: 'floor' },
  { id: 'server-laptop', file: 'prop_laptop.glb', x: -20.5, y: 1.2, z: -11.6, rotY: 0, halfW: 0.2, halfD: 0.15, group: 'server-cabinet', mount: 'desk' },
  { id: 'server-obelisk', file: null, x: SERVER_OBELISK_POS[0], y: 0, z: SERVER_OBELISK_POS[1], rotY: 0, halfW: 0.4, halfD: 0.4, group: 'server-obelisk', mount: 'floor' },
  { id: 'server-trashcan', file: 'prop_trashcan.glb', x: -22.7, y: 0, z: -12, rotY: 0, halfW: 0.2, halfD: 0.2, group: 'server-trashcan', mount: 'floor', decor: true },
  { id: 'server-plant', file: 'prop_plantSmall1.glb', x: -18.5, y: 0, z: -10.5, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'server-plant', mount: 'floor', decor: true },
]

/** Reception: small desk + 2 chairs + plant flanking the entry gap. */
export const RECEPTION_FURNITURE: PropPlacement[] = [
  { id: 'reception-desk', file: 'prop_desk.glb', x: -2.8, y: 0, z: 13.4, rotY: 90, halfW: 0.5, halfD: 0.75, group: 'reception-desk', mount: 'floor', scale: 1.3 },
  { id: 'reception-papers', file: 'prop_paperStack.glb', x: -2.8, y: DESK_SURFACE_Y, z: 13.2, rotY: 20, halfW: 0.15, halfD: 0.15, group: 'reception-desk', mount: 'desk' },
  { id: 'reception-chair', file: 'prop_chairDesk.glb', x: -3.7, y: 0, z: 13.4, rotY: 90, halfW: 0.3, halfD: 0.3, group: 'reception-chair', mount: 'floor', scale: 1.3 },
  { id: 'reception-guest-chair', file: 'prop_chairCushion.glb', x: 2.6, y: 0, z: 13.6, rotY: 270, halfW: 0.3, halfD: 0.3, group: 'reception-guest-chair', mount: 'floor', scale: 1.35 },
  { id: 'reception-plant', file: 'prop_pottedPlant.glb', x: 3.6, y: 0, z: 14.0, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'reception-plant', mount: 'floor', decor: true, scale: 1.4 },
]

/** Décor: whiteboard on the west wall, end-cap and corner plants, extras. */
export const DECOR_ITEMS: PropPlacement[] = [
  { id: 'decor-whiteboard', file: 'prop_whiteboard.glb', x: -17.3, y: 0, z: 3, rotY: 90, halfW: 0.2, halfD: 0.8, group: 'decor-whiteboard', mount: 'floor', decor: true },
  { id: 'decor-plant-cap-1', file: 'prop_pottedPlant.glb', x: -12.7, y: 0, z: -6, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'decor-plant-cap-1', mount: 'floor', decor: true },
  { id: 'decor-plant-cap-2', file: 'prop_pottedPlant.glb', x: 6.7, y: 0, z: -6, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'decor-plant-cap-2', mount: 'floor', decor: true },
  { id: 'decor-plant-cap-3', file: 'prop_pottedPlant.glb', x: -12.7, y: 0, z: 2, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'decor-plant-cap-3', mount: 'floor', decor: true },
  { id: 'decor-plant-cap-4', file: 'prop_plantSmall2.glb', x: -0.3, y: 0, z: 2, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'decor-plant-cap-4', mount: 'floor', decor: true },
  { id: 'decor-plant-corner-sw', file: 'prop_pottedPlant.glb', x: -17.2, y: 0, z: 11.2, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'decor-plant-corner-sw', mount: 'floor', decor: true },
  { id: 'decor-plant-corner-ne', file: 'prop_plantSmall1.glb', x: 22.8, y: 0, z: -14.2, rotY: 0, halfW: 0.25, halfD: 0.25, group: 'decor-plant-corner-ne', mount: 'floor', decor: true },
  // Printer along the west wall, between the whiteboard and the SW corner.
  { id: 'decor-printer', file: 'prop_printer.glb', x: -17.15, y: 0, z: 5.5, rotY: 90, halfW: 0.35, halfD: 0.4, group: 'decor-printer', mount: 'floor', decor: true },
  // Box stack in the empty nook between the server-room glass and the north
  // wall — two on the floor, one stacked on top (same group: may touch).
  { id: 'decor-box-stack-1', file: 'prop_cardboardBoxClosed.glb', x: -11.35, y: 0, z: -10.7, rotY: 0, halfW: 0.3, halfD: 0.3, group: 'decor-box-stack', mount: 'floor', decor: true },
  { id: 'decor-box-stack-2', file: 'prop_cardboardBoxOpen.glb', x: -11.3, y: 0, z: -9.7, rotY: 0, halfW: 0.3, halfD: 0.3, group: 'decor-box-stack', mount: 'floor', decor: true },
  { id: 'decor-box-stack-3', file: 'prop_cardboardBoxClosed.glb', x: -11.35, y: 0.38, z: -10.7, rotY: 0, halfW: 0.3, halfD: 0.3, group: 'decor-box-stack', mount: 'stack', decor: true },
]

/**
 * Full furniture inventory: one station unit per desk slot + all zone
 * placements. Station units are exact (no jitter) and cover desk + tucked
 * chair + monitor + keyboard, so their sub-props are not listed separately.
 */
export function computeFurnitureItems(): PropPlacement[] {
  const stations: PropPlacement[] = []
  for (let slot = 0; slot < GRID_SLOTS; slot++) {
    const [x, , z] = slotToPosition(slot)
    stations.push({
      id: `desk-station-${slot}`,
      file: 'prop_desk.glb',
      x, y: 0, z,
      rotY: slotRotationY(slot),
      halfW: STATION_HALF_W, halfD: STATION_HALF_D,
      group: `desk-station-${slot}`,
      mount: 'floor',
    })
  }
  return [
    ...stations,
    ...MEETING_FURNITURE,
    ...KITCHEN_FURNITURE,
    ...SERVER_FURNITURE,
    ...RECEPTION_FURNITURE,
    ...DECOR_ITEMS,
  ]
}

// ── Deterministic seeded jitter (décor only) ──────────────────────────────

function djb2(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0
  }
  return h
}

export const JITTER_MAX_OFFSET = 0.05 // ±5 cm
export const JITTER_MAX_ROT = 10      // ±10°

/**
 * Deterministic jitter for a décor item, seeded by its id. Always the same
 * output for the same id; bounded by ±JITTER_MAX_OFFSET / ±JITTER_MAX_ROT.
 */
export function decorJitter(id: string): { dx: number; dz: number; drotY: number } {
  const h = djb2(id)
  const r1 = (((h >>> 0) % 1000) / 999) * 2 - 1
  const r2 = (((h >>> 10) % 1000) / 999) * 2 - 1
  const r3 = (((h >>> 20) % 1000) / 999) * 2 - 1
  return {
    dx: r1 * JITTER_MAX_OFFSET,
    dz: r2 * JITTER_MAX_OFFSET,
    drotY: r3 * JITTER_MAX_ROT,
  }
}

// ── Desk clutter (LAYOUT-2) ───────────────────────────────────────────────
// Deterministic per desk slot id: an occupied desk gets 2-3 clutter props
// from a small pool; a few desks stay deliberately bare. Positions are in
// station-local space (occupant faces local -Z).

export interface DeskClutterItem {
  id: string
  file: string
  dx: number; dy: number; dz: number
  rotY: number
  scale: number
}

const CLUTTER_POOL: ReadonlyArray<Omit<DeskClutterItem, 'id' | 'rotY'>> = [
  { file: 'prop_paperStack.glb', dx: 0.5, dy: DESK_SURFACE_Y + 0.1, dz: -0.5, scale: 0.35 },
  { file: 'prop_deskPhone.glb', dx: -0.5, dy: DESK_SURFACE_Y + 0.09, dz: -0.55, scale: 0.18 },
  { file: 'prop_cardboardBoxClosed.glb', dx: 0.45, dy: 0, dz: -0.25, scale: 1.25 },
]

/** Slots whose hash lands on this modulo stay bare (no clutter). */
export const BARE_DESK_MODULO = 5

/**
 * Deterministic clutter for a desk slot. Same slot → same items, always.
 * Returns [] for bare desks; otherwise 2-3 pool items with seeded ±15°
 * rotation jitter.
 */
export function deskClutterForSlot(slot: number): DeskClutterItem[] {
  const h = djb2(`desk-clutter-${slot}`)
  if (h % BARE_DESK_MODULO === 0) return []
  const count = 2 + ((h >>> 3) % 2) // 2 or 3
  const start = (h >>> 5) % CLUTTER_POOL.length
  const items: DeskClutterItem[] = []
  for (let i = 0; i < count && i < CLUTTER_POOL.length; i++) {
    const base = CLUTTER_POOL[(start + i) % CLUTTER_POOL.length]
    const r = djb2(`desk-clutter-${slot}-${i}`)
    const rotY = (((r % 1000) / 999) * 2 - 1) * 15
    items.push({ id: `desk-clutter-${slot}-${i}`, rotY, ...base })
  }
  return items
}

// ── Character placement at a station (CHARACTERS-ALIVE) ───────────────────
// Station-local space: the occupant faces local -Z (monitor side). These
// constants are the single source of truth shared by the prop dressing
// (dressing.ts places the chair GLB at CHAIR_SEAT_LOCAL) and the character
// system (characters.ts seats the worker on that same chair).

/** Office chair position in station-local space (matches dressing.ts). */
export const CHAIR_SEAT_LOCAL = { dx: 0, dz: 0.4 } as const
/** Station-local yaw that faces the monitor (local -Z); the chair GLB uses the same 180. */
export const SEAT_LOCAL_YAW = 180
/** Standing spot beside/behind the chair (walk-up space) in station-local space. */
export const STAND_LOCAL = { dx: 0.9, dz: 0.9 } as const

/**
 * Rotate a station-local XZ offset into world space for a station with world
 * yaw `rotYDeg`. Same convention the station system has always used.
 */
export function rotateStationOffset(dx: number, dz: number, rotYDeg: number): [number, number] {
  const rad = (rotYDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return [dx * cos + dz * sin, -dx * sin + dz * cos]
}

export interface CharacterPlacement {
  x: number
  y: number
  z: number
  /** World yaw in degrees. */
  yaw: number
}

/**
 * World transform for a character seated on the station's chair, facing the
 * monitor. Derived purely from the station position + slot rotation and the
 * shared chair placement data.
 */
export function seatPlacement(
  stationPos: readonly [number, number, number],
  stationRotY: number,
): CharacterPlacement {
  const [ox, oz] = rotateStationOffset(CHAIR_SEAT_LOCAL.dx, CHAIR_SEAT_LOCAL.dz, stationRotY)
  return {
    x: stationPos[0] + ox,
    y: stationPos[1], // seat on the station's own floor (agents now span L0–L3)
    z: stationPos[2] + oz,
    yaw: (stationRotY + SEAT_LOCAL_YAW) % 360,
  }
}

/**
 * World transform for a character standing beside the station (the readable
 * "waiting" signal — stood up from the chair). Standing changes the position,
 * not the work focus: the character still faces the monitor at local -Z.
 */
export function standPlacement(
  stationPos: readonly [number, number, number],
  stationRotY: number,
): CharacterPlacement {
  const [ox, oz] = rotateStationOffset(STAND_LOCAL.dx, STAND_LOCAL.dz, stationRotY)
  return {
    x: stationPos[0] + ox,
    y: stationPos[1], // stand on the station's own floor (agents now span L0–L3)
    z: stationPos[2] + oz,
    yaw: (stationRotY + SEAT_LOCAL_YAW) % 360,
  }
}

// ── Micro-behavior signal functions (LAYOUT-2) ────────────────────────────
// Pure, deterministic functions of time — cheap to evaluate per frame and
// unit-testable without PlayCanvas.

/**
 * Irregular seeded blink level for a server-rack indicator. Steps between
 * bright/dim states on a per-index cadence with hashed on/off decisions —
 * never a smooth sine. Bounded to [0.15, 1.0].
 */
export function serverIndicatorLevel(index: number, timeSeconds: number): number {
  const period = 0.35 + 0.13 * ((index * 7 + 3) % 5)
  const step = Math.floor(timeSeconds / period)
  const r = djb2(`indicator-${index}-${step}`)
  const on = r % 100 < 60
  const jitter = (((r >>> 8) % 100) / 100) * 0.15
  return on ? 0.85 + jitter : 0.15 + jitter * 0.5
}

// ── Stable hash-slot allocator ────────────────────────────────────────────

/**
 * Stable hash-based slot allocator. Assigns each runId to a desk slot by
 * djb2 hash with linear probing. Evicts departed IDs, preserves existing
 * assignments — no station ever teleports.
 */
export class StationLayout {
  private assignments = new Map<string, number>()
  private occupied = new Set<number>()

  /**
   * Slot geometry is injectable so the same stable hash-slot allocator can drive
   * either the single L0 grid (defaults) or a multi-floor slot set (see
   * floorPrograms.ts) — the assign/evict logic is identical, only the slot count
   * and slot→(position, rotation) mapping differ.
   */
  constructor(
    private readonly slotCount: number = GRID_SLOTS,
    private readonly posFn: (slot: number) => [number, number, number] = slotToPosition,
    private readonly rotFn: (slot: number) => number = slotRotationY,
  ) {}

  assign(runIds: readonly string[]): Map<string, [number, number, number]> {
    const idSet = new Set(runIds)

    // Evict IDs no longer active — free their slots
    for (const [id, slot] of [...this.assignments.entries()]) {
      if (!idSet.has(id)) {
        this.occupied.delete(slot)
        this.assignments.delete(id)
      }
    }

    // Assign slots to new IDs
    for (const id of runIds) {
      if (this.assignments.has(id)) continue
      const h = djb2(id) % this.slotCount
      let slot = h
      let probe = 0
      while (this.occupied.has(slot) && probe < this.slotCount) {
        probe++
        slot = (h + probe) % this.slotCount
      }
      this.occupied.add(slot)
      this.assignments.set(id, slot)
    }

    const map = new Map<string, [number, number, number]>()
    for (const id of runIds) {
      const slot = this.assignments.get(id)
      if (slot !== undefined) map.set(id, this.posFn(slot))
    }
    return map
  }

  /** Slot currently assigned to a runId (for per-slot rotation lookup). */
  getSlot(id: string): number | undefined {
    return this.assignments.get(id)
  }

  /** World Y rotation (degrees) for a runId's assigned slot. */
  getRotation(id: string): number {
    const slot = this.assignments.get(id)
    return slot !== undefined ? this.rotFn(slot) : 0
  }
}
